import * as Types from '../gql/_types';

import { ExtensionModule } from "./init";
import { mapArrayToStateObject } from "utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'state/_types';
import { subscriptions } from 'state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'state/gql/utils';
import { SetMetaInput } from 'state/Module';

const Module = ExtensionModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetExtensionsQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.extensions.errors = [];
    state.extensions.loading = true;
    try {
        let { limit, offset, sortKey, sortOrder } = state.extensions.meta;
        let originalPayload = { limit, offset, order_by: { [sortKey]: sortOrder } };
        let { meta, items } = await effects.gql.queries.GetExtensions({...originalPayload, ...finalPayload})
        state.extensions.meta.total = meta.aggregate?.count || 0;
        state.extensions.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.extensions.errors = handleGraphqQLErrorResponse(e);
    }
    state.extensions.loading = false;
}

export const resetState: Action = ({state}) => {
    state.extensions.errors = []
    state.extensions.loading = false
    state.extensions.items = {}
    state.extensions.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.extensions.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.extensions.resetMeta()
    actions.extensions.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.extensions.errors = [];
    state.extensions.loadingMore = true;
    try {
        let { limit, sortKey, sortOrder, page } = state.extensions.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: { [sortKey]: sortOrder } };
        let { meta, items } = await effects.gql.queries.GetExtensions({...originalPayload, ...finalPayload})
        state.extensions.meta.total = meta.aggregate?.count || 0;
        state.extensions.meta.page = newPage;
        state.extensions.meta.offset = newOffset;
        state.extensions.items = { ...state.extensions.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.extensions.errors = handleGraphqQLErrorResponse(e);
    }
    state.extensions.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetExtensionsQueryVariables> = async ({state, effects}, payload) => {
    state.extensions.errors = [];
    state.extensions.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetExtensions(payload)
        state.extensions.meta.total = meta.aggregate?.count || 0;
        state.extensions.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.extensions.errors = handleGraphqQLErrorResponse(e);
    }
    state.extensions.loading = false;
}

export const getItem: AsyncAction<Types.GetExtensionQueryVariables,Types.GetExtensionQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetExtension(payload);
        return item;
    } catch (e: any) {
        state.extensions.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetExtensionQueryVariables> = async ({state, actions}, payload) => {
    state.extensions.loading = true;
    try {
        let item = await actions.extensions.getItem(payload);
        if (item) {
            state.extensions.currentItemId = item.id;
            state.extensions.items[item.id] = item;
        }
    } catch (e: any) {
        state.extensions.errors = handleGraphqQLErrorResponse(e);
    }
    state.extensions.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.extensions.currentItemId = payload
}

export const setMeta: Action<SetMetaInput<Types.ExtensionFragment>> = async ({state}, meta) => {
    state.extensions.meta = {
        ...state.extensions.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateExtensionMutationVariables['object'],Types.CreateExtensionMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateExtensionMutationVariables['object']>(payload);
    state.extensions.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateExtension({object:finalPayload});
        return item;
    } catch (e: any) {
        state.extensions.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateExtensionMutationVariables,Types.UpdateExtensionMutation['item']> = async ({state, effects}, payload) => {
    state.extensions.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateExtension(payload);
        return item;
    } catch (e: any) {
        state.extensions.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteExtensionMutationVariables,Types.DeleteExtensionMutation['item']> = async ({state, effects}, payload) => {
    state.extensions.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteExtension(payload);
        return item;
    } catch (e: any) {
        state.extensions.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetExtensionsMetaSubscriptionVariables> = async ({state, actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetExtensionsMetaSubscription, Types.SyncGetExtensionsMetaSubscriptionVariables>(
        'syncGetExtensionsMeta', 
        subscriptions.SyncGetExtensionsMeta, 
        { where }, 
        actions.extensions.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetExtensionsMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetExtensionsMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.extensions.errors = [ ...state.extensions.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.extensions.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetExtensionsSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, sortKey, sortOrder } = state.extensions.meta;
    let originalPayload = { limit, offset, order_by: { [sortKey]: sortOrder } };
    state.extensions.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetExtensionsSubscription, Types.SyncGetExtensionsSubscriptionVariables>(
        'syncGetExtensions', 
        subscriptions.SyncGetExtensions, 
        {...originalPayload, ...payload}, 
        actions.extensions.onItemsData,
        () => { state.extensions.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetExtensions');
    state.extensions.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetExtensionsSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.extensions.errors = [ ...state.extensions.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.extensions.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetExtensionsSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.extensions.syncGetItems(finalPayload);
    actions.extensions.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.extensions.unsubItemsData();
    actions.extensions.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetExtensionSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.extensions.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetExtensionSubscription, Types.SyncGetExtensionSubscriptionVariables>(
        'syncGetExtension', 
        subscriptions.SyncGetExtension, 
        payload, 
        actions.extensions.onItemData,
        () => { state.extensions.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetExtension');
    state.extensions.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetExtensionSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.extensions.errors = [ ...state.extensions.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.extensions.currentItemId = payload.data.item.id;
        state.extensions.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetExtensionSubscriptionVariables> = async ({actions}, payload) => {
    actions.extensions.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.extensions.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};